import * as React from 'react';
import { RouteComponentProps } from 'react-router';

import { Card } from '@blueprintjs/core';
import ProjectType from 'src/models/ProjectType';
import PagedResultSet from 'src/models/common/PagedResultSet';
import { ODataOptions } from 'src/util/ODataBuilder';
import CardContainer from 'src/components/common/CardContainer';
import Client from 'src/models/Client';
import { SelectItem } from 'src/models/common/SelectItem';
import { Form, AnyObject } from 'react-final-form';
import State from 'src/models/State';
import PropertyUtil from 'src/util/PropertyUtil';
import arrayMutators from 'final-form-arrays';
import ProjectModel from 'src/models/Project';

import Textbox from 'src/components/controls/forms/redesign/FormTextbox';
import Select from 'src/components/controls/forms/redesign/FormFilterSelect';
import Button from '../controls/forms/redesign/FormButton';
import Office from 'src/models/Office';
import County from 'src/models/County';
import { IRootState } from 'src/reducers';
import selectors from 'src/selectors';
import actions from 'src/actions';
import { connect } from 'react-redux';
import TagContainer from '../counties/TagContainer';
import IOwner from 'src/models/IOwner';
import User from 'src/models/User';

interface IAddProjectPageStoreProps {
    client: (id: number) => Client;
    isProjectTypesLoading: boolean;
    projectTypes: ProjectType[];
    tractTypes: SelectItem[];
    states: PagedResultSet<State>;
    stateCounties: PagedResultSet<County>;
    offices: Office[];
    pcapmList: User[];
}

export interface IAddProjectPageDispatchProps {
    getClient: (id: number) => void;
    getProjectTypes: (options: ODataOptions) => void;
    getTractTypes: (clientNumber: number) => void;
    getStates: (options: ODataOptions) => void;
    getOffices: () => void;
    getStateCounties: (stateId: number, options: ODataOptions) => void;
    getPCAPMList: () => void;
    createProject: (project: ProjectModel) => void;
    initializeCounties: () => void;
    createBusinessOwner: (clientNumber: number, projectNumber: number, owner: IOwner) => void;
}

type IAddProjectPageStateProps = {
    isStateSelected: boolean;
};

export type IAddProjectPageProps = RouteComponentProps<{ clientNumber?: string }> & IAddProjectPageStoreProps & IAddProjectPageDispatchProps;

class AddProjectPage extends React.Component<IAddProjectPageProps, IAddProjectPageStateProps> {
    constructor(props: IAddProjectPageProps) {
        super(props);
        this.state = {
            isStateSelected: false,
        };
    }

    public componentDidMount() {
        this.props.initializeCounties();
        this.props.getClient(parseInt(this.props.match.params.clientNumber, 10));
        this.props.getProjectTypes(new ODataOptions());
        this.props.getTractTypes(parseInt(this.props.match.params.clientNumber, 10));
        this.props.getStates(new ODataOptions().addPaging(1, 100));
        this.props.getOffices();
        this.props.getPCAPMList();
    }

    handleSubmit = (project: ProjectModel) => {
        project.client = this.props.client(parseInt(this.props.match.params.clientNumber, 10))
            ? this.props.client(parseInt(this.props.match.params.clientNumber, 10))
            : new Client();
        project.client.id = parseInt(this.props.match.params.clientNumber, 10);
        this.props.createProject(project);
        // create dudley owner
        // create client owner
    };

    stateSelected = (id: number) => {
        if (id === -1) {
            this.setState({ isStateSelected: false });
        } else {
            this.props.getStateCounties(id, new ODataOptions());
            this.setState({ isStateSelected: true });
        }
    };

    validate = (project: AnyObject) => {
        const errors: AnyObject = {};

        if (!project.name) {
            errors.name = 'Name is Required';
        }

        if (!project.landSystemId) {
            errors.landSystemId = 'Land System is required';
        }

        if (!project.state || !project.state.id) {
            errors.state = {};
            errors.state.id = 'State is required';
        }

        if (!project.managingOfficeId) {
            errors.managingOfficeId = 'Office is required';
        }

        return errors;
    };

    render() {
        const client = this.props.client(parseInt(this.props.match.params.clientNumber, 10));
        const projectTypes = PropertyUtil.SafeGet(() => this.props.projectTypes);
        const tractTypes = PropertyUtil.SafeGet(() => this.props.tractTypes);
        const selectProjectTypes = projectTypes ? projectTypes.map((projectType: ProjectType) => new SelectItem(projectType.id, projectType.name)) : undefined;
        const selectLandSystems = [
            new SelectItem(1, 'Jeffersonian'),
            new SelectItem(3, 'West Texas'),
            new SelectItem(4, 'East Texas'),
            new SelectItem(2, 'Eastern'),
            new SelectItem(1001, 'Right of Way')
        ];

        const states = PropertyUtil.SafeGet(() => this.props.states.items);
        const selectStates = states ? states.map((state: State) => new SelectItem(state.id, state.name)) : undefined;

        const offices = PropertyUtil.SafeGet(() => this.props.offices);
        const managingOfficesList = offices ? offices.map((office: Office) => new SelectItem(office.id, office.name)) : undefined;

        const stateCounties = PropertyUtil.SafeGet(() => this.props.stateCounties.items);
        const selectStateCounties = stateCounties ? stateCounties.map((c: County) => new SelectItem(c.id, c.name)) : undefined;

        const pcapms = PropertyUtil.SafeGet(() => this.props.pcapmList);
        const selectPcapms = pcapms ? pcapms.map((p: User) => new SelectItem(p.id, `${p.firstName} ${p.lastName}`)) : undefined;

        return (
            <CardContainer>
                <Card elevation={1}>
                    <h3>Add Project to {client && client.cloakName}</h3>
                    <Form
                        onSubmit={this.handleSubmit}
                        mutators={{ ...arrayMutators }}
                        validate={this.validate}
                        initialValues={{ county: selectStateCounties && selectStateCounties.length === 1 ? selectStateCounties[0].id : undefined }}
                        render={({ handleSubmit, values, form }) => {
                            // const isShadow = values.isShadow;

                            return (
                                <form onSubmit={handleSubmit}>
                                    <Textbox label="Project Name" name="name" />
                                    {/* <FormCheckbox label="Shadow" name="isShadow" />
                                    {isShadow && (
                                        <>
                                            <Textbox label="Shadow Project Name" name="cloakName" />
                                            <Textbox label="Shadow Client Name" name="client.cloakName" />
                                        </>
                                    )} */}

                                    <Select label="Project Type" name="projectType.id" items={selectProjectTypes} defaultOption="Select Project Type" />
                                    <Select label="Tract Type" name="tractTypeId" items={tractTypes} defaultOption="Select Tract Type" />
                                    <Select label="Land System" name="landSystemId" items={selectLandSystems} defaultOption="Select Land System" />
                                    <Select
                                        label="State"
                                        name="state.id"
                                        items={selectStates}
                                        defaultOption="Select State"
                                        onChange={this.stateSelected}
                                    />
                                    <TagContainer
                                        label="Counties"
                                        fieldArrayName="counties"
                                        fieldAddArrayName="addCounties"
                                        fieldDeleteArrayName="deleteCounties"
                                        initialValueName="county"
                                        isTagCreatable={false}
                                        form={form}
                                        isDisabled={!this.state.isStateSelected}
                                        disabledMessage="Please select a state first..."
                                        selectTags={selectStateCounties ? selectStateCounties : new Array<SelectItem>()}
                                    />
                                    <Select label="Managing Office" name="managingOfficeId" items={managingOfficesList} />
                                    <Select label="PCAPM" name="pcapmId" items={selectPcapms} />
                                    <br />
                                    <div style={{ marginTop: '1rem' }}>
                                        <Button type="submit" text="Add Project" />
                                    </div>
                                </form>
                            );
                        }}
                    />
                </Card>
            </CardContainer>
        );
    }
}

const mapStateToProps = (state: IRootState): IAddProjectPageStoreProps => ({
    stateCounties: selectors.county.stateCounties(state),
    client: (id: number) => selectors.client.client(state),
    isProjectTypesLoading: selectors.project.isLoading(state),
    projectTypes: selectors.project.projectTypes(state),
    tractTypes: selectors.project.tractTypes(state),
    states: selectors.state.states(state),
    offices: selectors.office.offices(state),
    pcapmList: selectors.user.pcapmUsers(state)
});

const mapDispatchToProps: IAddProjectPageDispatchProps = {
    initializeCounties: () => actions.county.initializeCounties(),
    getStateCounties: (stateId: number, options: ODataOptions) => actions.county.getStateCounties(stateId, options),
    getClient: (id: number) => actions.client.getClient(id),
    getProjectTypes: (options: ODataOptions) => actions.project.getProjectTypes(options),
    getTractTypes: (clientNumber: number) => actions.project.getTractTypes(clientNumber),
    getStates: (options: ODataOptions) => actions.state.getStates(options),
    getOffices: () => actions.office.getOffices(),
    getPCAPMList: () => actions.user.getUsers(),
    createProject: (project: ProjectModel) => actions.project.createProject(project),
    createBusinessOwner: (clientNumber: number, projectNumber: number, owner: IOwner) =>
        actions.owner.createBusinessOwner(clientNumber, projectNumber, owner),
};

export default connect(mapStateToProps, mapDispatchToProps)(AddProjectPage);



// WEBPACK FOOTER //
// ./src/components/project/AddProjectPage.tsx